import { Helmet } from 'react-helmet-async'
import React, { useEffect } from 'react'
import { Breadcrumbs, Link, Typography, Box, Container } from '@mui/material'
import { useLocation } from 'react-router-dom'
import {
  getCurrentDomain,
  getBaseUrl,
  getSEOForRoute,
} from '../utils/seoConfig'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'

export const BreadcrumbComponent = ({
  currentItem,
  itemType, // 'article', 'decision', 'guidance', 'tax-treatie'
  lawInfo,
  country = 'UAE',
}) => {
  const generateBreadcrumbs = () => {
    const breadcrumbs = [
      {
        name: 'Home',
        url: '/',
        position: 1,
      },
    ]

    // Add search breadcrumb based on item type
    if (itemType === 'tax-treatie') {
      breadcrumbs.push({
        name: 'Tax Treaties (DTAA)',
        url: `/search/${country}/Tax-Treaties`,
        position: 2,
      })
    } else if (itemType === 'blog') {
      breadcrumbs.push({
        name: 'Blogs',
        url: `/blogs?section=team`,
        position: 2,
      })
    } else {
      breadcrumbs.push({
        name: `Search ${itemType.charAt(0).toUpperCase() + itemType.slice(1)}s`,
        url: `/search-across-law?country=${country}&law=${
          lawInfo?.lawShortName?.split('-').slice(1).join('-') || ''
        }`,
        position: 2,
      })
    }

    // Add law-specific breadcrumb if available (not for tax treaties)
    if (itemType !== 'tax-treatie' && lawInfo?.lawFullName) {
      breadcrumbs.push({
        name:
          lawInfo.lawFullName.length > 40
            ? lawInfo.lawFullName.substring(0, 40).replace(/<[^>]*>/g, '') +
              '...'
            : lawInfo.lawFullName.replace(/<[^>]*>/g, ''),
        url: `/laws/${country.toLowerCase()}/${
          lawInfo.lawShortName?.split('-').slice(1).join('-') || ''
        }`,
        position: 3,
      })
    }

    // Add current item
    const currentItemName =
      itemType === 'tax-treatie'
        ? `${currentItem.country1Name} - ${currentItem.country2Name} DTAA`
        : currentItem.title?.replace(/<[^>]*>/g, '') || // Remove HTML tags
          `${itemType} ${
            currentItem.number ||
            currentItem.uniqueCode ||
            currentItem.slug ||
            ''
          }`

    breadcrumbs.push({
      name:
        currentItemName.length > 60
          ? new DOMParser().parseFromString(
              // UAE FATCA: Foreign Account Tax Compliance Act (&quot;FATCA&quot;) Frequently Asked Questions
              currentItemName.substring(0, 60).replace(/<[^>]*>/g, ''),
              'text/html'
            ).documentElement.textContent + '...'
          : new DOMParser().parseFromString(
              currentItemName.replace(/<[^>]*>/g, ''),
              'text/html'
            ).documentElement.textContent,
      url: `/${itemType}s/${currentItem.slug}`,
      position: breadcrumbs.length + 1,
    })

    return breadcrumbs
  }

  const breadcrumbs = generateBreadcrumbs()

  // Generate structured data for breadcrumbs
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb) => ({
      '@type': 'ListItem',
      position: crumb.position,
      name: crumb.name,
      item: {
        '@type': 'WebPage',
        '@id': `${window.location.origin}${crumb.url}`,
        url: `${window.location.origin}${crumb.url}`,
      },
    })),
  }

  return (
    <>
      {/* Breadcrumb Container with proper styling */}
      <Box
        sx={{
          backgroundColor: '#f8f9fa', // Light background to match header
          borderBottom: '1px solid #e9ecef',
          py: 1.5,
          mb: 0, // Remove margin bottom to integrate better
        }}
      >
        <Container maxWidth='lg'>
          <Breadcrumbs
            aria-label='breadcrumb'
            separator={
              <NavigateNextIcon fontSize='small' sx={{ color: '#6c757d' }} />
            }
            sx={{
              fontSize: '0.875rem',
              '& .MuiBreadcrumbs-separator': {
                color: '#6c757d',
                mx: 1,
              },
              '& .MuiBreadcrumbs-ol': {
                flexWrap: 'wrap',
              },
            }}
          >
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1

              return isLast ? (
                <Typography
                  key={index}
                  color='text.primary'
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: '#495057', // Darker text for current page
                    lineHeight: 1.4,
                    wordBreak: 'break-word',
                  }}
                  title={crumb.name} // Show full text on hover
                >
                  {crumb.name}
                </Typography>
              ) : (
                <Link
                  key={index}
                  color='inherit'
                  href={crumb.url}
                  underline='hover'
                  sx={{
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    color: '#5948CA', // Blue color for links
                    fontWeight: 500,
                    lineHeight: 1.4,
                    display: 'inline-block',
                    wordBreak: 'break-word',
                    transition: 'color 0.2s ease',
                    '&:hover': {
                      textDecoration: 'underline',
                      color: '#2F438D', // Darker blue on hover
                    },
                  }}
                  title={crumb.name} // Show full text on hover
                >
                  {crumb.name}
                </Link>
              )
            })}
          </Breadcrumbs>
        </Container>
      </Box>

      {/* Structured Data for SEO */}
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema, null, 0),
        }}
      />
    </>
  )
}

const SEO = ({
  title,
  description,
  canonical,
  keywords,
  schemaMarkup,
  ogImage = '/web-app-manifest-512x512.png',
  articleType = 'article', // 'article', 'decision', 'guidance', 'treaty'
  publishedDate,
  modifiedDate,
  country,
  lawName,
  useDomainSEO = false, // flag to use domain-specific SEO
}) => {
  // Google Search Console Error: Duplicate without user-selected canonical
  // Remove the problematic useEffect that was causing conflicts
  // The Helmet component will handle all meta tag updates properly

  // // Force a re-render of meta tags when route changes
  // useEffect(() => {
  //   // Small delay to ensure proper cleanup and re-rendering
  //   const timeoutId = setTimeout(() => {
  //     // Update document title immediately as backup
  //     if (title) {
  //       document.title = title
  //     }
  //     if (description) {
  //       let metaDescription = document.querySelector('meta[name="description"]')
  //       if (metaDescription) {
  //         metaDescription.setAttribute('content', description)
  //       } else {
  //         // Create the meta tag if it doesn't exist
  //         metaDescription = document.createElement('meta')
  //         metaDescription.name = 'description'
  //         metaDescription.content = description
  //         document.head.appendChild(metaDescription)
  //       }
  //     }

  //     if (canonical) {
  //       let canonicalLink = document.querySelector('link[rel="canonical"]')

  //       if (canonicalLink) {
  //         canonicalLink.setAttribute('href', canonical)
  //       } else {
  //         // Create the canonical link if it doesn't exist
  //         canonicalLink = document.createElement('link')
  //         canonicalLink.rel = 'canonical'
  //         canonicalLink.href = canonical
  //         document.head.appendChild(canonicalLink)
  //       }
  //     }
  //   }, 0)
  //   return () => clearTimeout(timeoutId)
  // }, [title, description, canonical])

  const location = useLocation()
  const currentDomain = getCurrentDomain()
  const baseUrl = getBaseUrl()
  // If useDomainSEO is true, load SEO from configuration
  let finalTitle = title
  let finalDescription = description
  let finalKeywords = keywords
  let finalCanonical = canonical
  if (useDomainSEO && !title) {
    const routeSEO = getSEOForRoute(location.pathname, currentDomain)
    finalTitle = routeSEO.title || title
    finalDescription = routeSEO.description || description
    finalKeywords = routeSEO.keywords || keywords
    finalCanonical = routeSEO.canonical || canonical
  }

  // Generate keywords automatically if not provided
  const generateKeywords = () => {
    if (finalKeywords) return finalKeywords

    const baseKeywords = []

    // Add country-specific keywords
    if (country) {
      baseKeywords.push(`${country} tax law`)
      if (country === 'UAE') {
        baseKeywords.push('UAE corporate tax', 'Emirates tax law')
      }
    }

    // Add law-specific keywords
    if (lawName) {
      if (lawName.includes('cit')) {
        baseKeywords.push('corporate income tax', 'CIT law')
      }
      if (lawName.includes('vat')) {
        baseKeywords.push('value added tax', 'VAT law')
      }
    }

    // Add type-specific keywords
    if (articleType === 'decision') {
      baseKeywords.push('tax decision', 'legal ruling')
    } else if (articleType === 'guidance') {
      baseKeywords.push('tax guidance', 'compliance guide')
    } else if (articleType === 'treaty') {
      baseKeywords.push('tax treaty', 'double taxation agreement')
    }

    baseKeywords.push('GCC tax laws', 'tax compliance')

    return baseKeywords.slice(0, 8).join(', ') // Limit to 8 keywords
  }

  // Ensure canonical URL is absolute
  const getAbsoluteCanonical = () => {
    if (!finalCanonical) return `${baseUrl}${location.pathname}`

    // If it's already absolute, return as is
    if (finalCanonical.startsWith('http')) {
      return finalCanonical
    }

    // If it's relative, make it absolute
    return `${baseUrl}${finalCanonical.startsWith('/') ? finalCanonical : '/' + finalCanonical}`
  }

  const absoluteCanonical = getAbsoluteCanonical()

  const fullImageUrl = ogImage.startsWith('http')
    ? ogImage
    : `${baseUrl}${ogImage}`
  const finalKeywordsValue = finalKeywords || generateKeywords()
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name='description' content={finalDescription} />
      <meta name='keywords' content={finalKeywordsValue} />
      {absoluteCanonical && <link rel='canonical' href={absoluteCanonical} />}

      {/* Add conditional Robots and Indexing meta */}
      {window.location.pathname.includes('/oauth2/callback') ? (
        <meta name='robots' content='noindex, nofollow' />
      ) : (
        <meta
          name='robots'
          content='index, follow, max-snippet:-1, max-image-preview:large'
        />
      )}
      <meta name='googlebot' content='index, follow' />

      {/* Author and Copyright */}
      <meta name='author' content='Team GTL' />
      <meta
        name='copyright'
        content={`© ${new Date().getFullYear()} ${currentDomain.split('.')[0].toUpperCase()}. All rights reserved.`}
      />

      {/* Open Graph for Social Media */}
      <meta property='og:title' content={finalTitle} />
      <meta property='og:description' content={finalDescription} />
      <meta property='og:image' content={fullImageUrl} />
      {absoluteCanonical && (
        <meta property='og:url' content={absoluteCanonical} />
      )}
      <meta property='og:type' content='article' />
      <meta
        property='og:site_name'
        content={currentDomain.split('.')[0].toUpperCase()}
      />
      <meta property='og:locale' content='en_US' />
      {publishedDate && (
        <meta property='article:published_time' content={publishedDate} />
      )}
      {modifiedDate && (
        <meta property='article:modified_time' content={modifiedDate} />
      )}
      <meta property='article:author' content='Team GTL' />
      <meta property='article:section' content='Tax Law' />
      {country && (
        <meta property='article:tag' content={`${country} Tax Law`} />
      )}

      {/* Twitter Card */}
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={finalTitle} />
      <meta name='twitter:description' content={finalDescription} />
      <meta name='twitter:image' content={fullImageUrl} />
      <meta
        name='twitter:site'
        content={`@${currentDomain.split('.')[0].toUpperCase()}`}
      />

      {/* Additional Legal/Tax specific meta tags */}
      <meta name='document-type' content='Legal Document' />
      {country && <meta name='jurisdiction' content={country} />}
      <meta name='content-language' content='en' />

      {/* Structured Data (Schema Markup) */}
      {schemaMarkup && (
        <script type='application/ld+json'>
          {JSON.stringify(schemaMarkup, null, 0)}
        </script>
      )}
    </Helmet>
  )
}

export default SEO
